// Master-data migration configs — mirrors backend master_registry.py
import {
    MASTER_ASSET_CATEGORY,
} from "../../constants/api";

const trimmed = (v) => (v === null || v === undefined ? "" : String(v).trim());

const text = (max = 50) => (v) => {
    const s = trimmed(v);
    if (!s) return "This field is required";
    if (s.length > max) return `Must be ${max} characters or fewer`;
    return null;
};

const optionalText = (max = 150) => (v) => {
    const s = trimmed(v);
    return s.length > max ? `Must be ${max} characters or fewer` : null;
};

const inEnum = (options) => (v) =>
    options.some((o) => o.text === trimmed(v)) ? null : "Select a valid option";

const boolField = (v) => {
    const s = trimmed(v);
    if (!s) return null;
    const ok = ["true", "false", "yes", "no", "1", "0", "y", "n"].includes(s.toLowerCase());
    return ok ? null : "Use true or false";
};

const TIME_RE = /^(\d{1,2}:\d{2}(:\d{2})?|\d{1,2}:\d{2}\s*(AM|PM))$/i;
const timeRequired = (v) => (TIME_RE.test(trimmed(v)) ? null : "Use HH:MM:SS or h:mm AM/PM");
const timeOptional = (v) => {
    const s = trimmed(v);
    return s && !TIME_RE.test(s) ? "Use HH:MM:SS or h:mm AM/PM" : null;
};

const inOptions = (sourceKey, optionSources) => (v, _row, opts = {}) => {
    const list = opts[sourceKey] || [];
    const label = optionSources[sourceKey].label;
    return list.some((o) => o[label] === trimmed(v)) ? null : "Select a valid option";
};

export const MASTER_OPTION_SOURCES = {
    asset_category: { label: "category_name" },
};

const expenseEnum = [
    { value: "general", text: "general" },
    { value: "travel", text: "travel" },
];

const boolEnum = [
    { value: "true", text: "true" },
    { value: "false", text: "false" },
];

const SNAKE_TO_PASCAL = {
    user_group_name: "UserGroupName",
    employee_type_name: "EmployeeTypeName",
    department_name: "DepartmentName",
    designation_name: "DesignationName",
    employee_group_name: "EmployeeGroupName",
    is_contract_employee: "IsContractEmployee",
    reimbursement_type_name: "ReimbursementTypeName",
    expense_category: "ExpenseCategory",
    asset_type_name: "AssetTypeName",
    category_name: "CategoryName",
    asset_sub_category_name: "AssetSubCategoryName",
    asset_category_name: "AssetCategory",
    shift_name: "ShiftName",
    shift_timings_from: "ShiftFrom",
    shift_timings_to: "ShiftTo",
    total_shift_hrs: "TotalShiftHrs",
    total_break_hrs: "TotalBreakHrs",
    break_1st_start_time: "Break1Start",
    break_1st_end_time: "Break1End",
    lunch_start_time: "LunchStart",
    lunch_end_time: "LunchEnd",
    break_2nd_start_time: "Break2Start",
    break_2nd_end_time: "Break2End",
    notes: "Notes",
};

const buildConfig = ({
    type,
    slug,
    label,
    subtitle,
    fields,
    requiredHeader,
    optionSources = {},
    fetchOptions,
    isBundle = false,
}) => {
    const validateRow = (row, options = {}) => {
        const errors = {};
        for (const f of fields) {
            const msg = f.validate?.(row?.[f.key], row, options);
            if (msg) errors[f.key] = msg;
        }
        if (Array.isArray(row?.comments)) {
            for (const c of row.comments) {
                const key = SNAKE_TO_PASCAL[c.key];
                if (key && row[key] === c.value && !errors[key]) errors[key] = c.message;
            }
        }
        return errors;
    };

    const rowHasErrors = (row, options) => Object.keys(validateRow(row, options)).length > 0;

    const headerToKey = (header) => String(header || "").replace(/\*/g, "").replace(/\s+/g, "");

    return {
        type,
        slug,
        label,
        subtitle,
        fields,
        requiredHeader,
        optionSources,
        fetchOptions,
        validateRow,
        rowHasErrors,
        headerToKey,
        SNAKE_TO_PASCAL,
        isBundle,
    };
};

const notesField = { key: "Notes", label: "Notes", required: false, kind: "text", validate: optionalText(150) };

export const MASTER_MIGRATION_TYPES = {
    user_group: buildConfig({
        type: "user_group",
        slug: "user-group",
        label: "User Group",
        subtitle: "Bulk-import user groups with default permission modules.",
        requiredHeader: "User Group Name *",
        fields: [
            { key: "UserGroupName", label: "User Group Name", required: true, kind: "text", validate: text(60) },
            notesField,
        ],
    }),
    employee_type: buildConfig({
        type: "employee_type",
        slug: "employee-type",
        label: "Employee Type",
        subtitle: "Bulk-import employee types from a spreadsheet.",
        requiredHeader: "Employee Type Name *",
        fields: [
            { key: "EmployeeTypeName", label: "Employee Type Name", required: true, kind: "text", validate: text(50) },
            notesField,
        ],
    }),
    department: buildConfig({
        type: "department",
        slug: "department",
        label: "Department",
        subtitle: "Bulk-import departments before employee migration.",
        requiredHeader: "Department Name *",
        fields: [
            { key: "DepartmentName", label: "Department Name", required: true, kind: "text", validate: text(50) },
            notesField,
        ],
    }),
    designation: buildConfig({
        type: "designation",
        slug: "designation",
        label: "Designation",
        subtitle: "Bulk-import designations before employee migration.",
        requiredHeader: "Designation Name *",
        fields: [
            { key: "DesignationName", label: "Designation Name", required: true, kind: "text", validate: text(50) },
            notesField,
        ],
    }),
    employee_group: buildConfig({
        type: "employee_group",
        slug: "employee-group",
        label: "Employee Group",
        subtitle: "Bulk-import employee groups from a spreadsheet.",
        requiredHeader: "Employee Group Name *",
        fields: [
            { key: "EmployeeGroupName", label: "Employee Group Name", required: true, kind: "text", validate: text(50) },
            { key: "IsContractEmployee", label: "Is Contract Employee", required: false, kind: "enum", enum: boolEnum, validate: boolField },
            notesField,
        ],
    }),
    reimbursement_type: buildConfig({
        type: "reimbursement_type",
        slug: "reimbursement-type",
        label: "Reimbursement Type",
        subtitle: "Bulk-import reimbursement types (general or travel).",
        requiredHeader: "Reimbursement Type Name *",
        fields: [
            { key: "ReimbursementTypeName", label: "Reimbursement Type Name", required: true, kind: "text", validate: text(50) },
            { key: "ExpenseCategory", label: "Expense Category", required: true, kind: "enum", enum: expenseEnum, validate: inEnum(expenseEnum) },
            notesField,
        ],
    }),
    asset_type: buildConfig({
        type: "asset_type",
        slug: "asset-type",
        label: "Asset Type",
        subtitle: "Bulk-import asset types from a spreadsheet.",
        requiredHeader: "Asset Type Name *",
        fields: [
            { key: "AssetTypeName", label: "Asset Type Name", required: true, kind: "text", validate: text(50) },
            notesField,
        ],
    }),
    asset_category: buildConfig({
        type: "asset_category",
        slug: "asset-category",
        label: "Asset Category",
        subtitle: "Bulk-import asset categories before sub categories.",
        requiredHeader: "Category Name *",
        fields: [
            { key: "CategoryName", label: "Category Name", required: true, kind: "text", validate: text(50) },
            notesField,
        ],
    }),
    asset_sub_category: buildConfig({
        type: "asset_sub_category",
        slug: "asset-sub-category",
        label: "Asset Sub Category",
        subtitle: "Bulk-import asset sub categories linked to an existing category.",
        requiredHeader: "Asset Sub Category Name *",
        optionSources: MASTER_OPTION_SOURCES,
        fetchOptions: async (fetch) => {
            const res = await fetch(MASTER_ASSET_CATEGORY, { drop_down: "True" });
            const list = res?.status === "success" && Array.isArray(res.data) ? res.data : [];
            return { asset_category: list };
        },
        fields: [
            { key: "AssetSubCategoryName", label: "Asset Sub Category Name", required: true, kind: "text", validate: text(50) },
            {
                key: "AssetCategory",
                label: "Parent Asset Category",
                required: true,
                kind: "select",
                source: "asset_category",
                validate: inOptions("asset_category", MASTER_OPTION_SOURCES),
            },
            notesField,
        ],
    }),
    shift: buildConfig({
        type: "shift",
        slug: "shift",
        label: "Shift Configuration",
        subtitle: "Bulk-import shift timings. Use HH:MM:SS for all time columns.",
        requiredHeader: "Shift Name *",
        fields: [
            { key: "ShiftName", label: "Shift Name", required: true, kind: "text", validate: text(50) },
            { key: "ShiftFrom", label: "Shift From", required: true, kind: "text", validate: timeRequired },
            { key: "ShiftTo", label: "Shift To", required: true, kind: "text", validate: timeRequired },
            { key: "TotalShiftHrs", label: "Total Shift Hrs", required: true, kind: "text", validate: timeRequired },
            { key: "TotalBreakHrs", label: "Total Break Hrs", required: true, kind: "text", validate: timeRequired },
            { key: "Break1Start", label: "Break1 Start", required: false, kind: "text", validate: timeOptional },
            { key: "Break1End", label: "Break1 End", required: false, kind: "text", validate: timeOptional },
            { key: "LunchStart", label: "Lunch Start", required: false, kind: "text", validate: timeOptional },
            { key: "LunchEnd", label: "Lunch End", required: false, kind: "text", validate: timeOptional },
            { key: "Break2Start", label: "Break2 Start", required: false, kind: "text", validate: timeOptional },
            { key: "Break2End", label: "Break2 End", required: false, kind: "text", validate: timeOptional },
            notesField,
        ],
    }),
};

const BUNDLE_TYPE_ORDER = [
    "user_group", "employee_type", "department", "designation", "employee_group",
    "reimbursement_type", "asset_type", "asset_category", "asset_sub_category", "shift",
];

const BUNDLE_TYPE_ALIASES = {
    user_group: "user_group", usergroup: "user_group", "user group": "user_group",
    employee_type: "employee_type", employeetype: "employee_type", "employee type": "employee_type",
    department: "department",
    designation: "designation",
    employee_group: "employee_group", employeegroup: "employee_group", "employee group": "employee_group",
    reimbursement_type: "reimbursement_type", reimbursementtype: "reimbursement_type",
    "reimbursement type": "reimbursement_type", "reimbursements type": "reimbursement_type",
    asset_type: "asset_type", assettype: "asset_type", "asset type": "asset_type",
    asset_category: "asset_category", assetcategory: "asset_category", "asset category": "asset_category",
    asset_sub_category: "asset_sub_category", assetsubcategory: "asset_sub_category",
    "asset sub category": "asset_sub_category", "asset subcategory": "asset_sub_category",
    shift: "shift", "shift configuration": "shift",
};

export const resolveBundleType = (raw) => {
    if (raw === null || raw === undefined || raw === "") return null;
    const key = trimmed(raw).toLowerCase().replace(/-/g, "_");
    const spaced = trimmed(raw).toLowerCase();
    return BUNDLE_TYPE_ALIASES[key] || BUNDLE_TYPE_ALIASES[spaced] || null;
};

const bundleTypeEnum = BUNDLE_TYPE_ORDER.map((t) => ({ value: t, text: t }));

const ENTITY_FIELD_TO_BUNDLE = {
    UserGroupName: "Name",
    EmployeeTypeName: "Name",
    DepartmentName: "Name",
    DesignationName: "Name",
    EmployeeGroupName: "Name",
    IsContractEmployee: "IsContractEmployee",
    ReimbursementTypeName: "Name",
    ExpenseCategory: "ExpenseCategory",
    AssetTypeName: "Name",
    CategoryName: "Name",
    AssetSubCategoryName: "Name",
    AssetCategory: "AssetCategory",
    ShiftName: "Name",
    ShiftFrom: "ShiftFrom",
    ShiftTo: "ShiftTo",
    TotalShiftHrs: "TotalShiftHrs",
    TotalBreakHrs: "TotalBreakHrs",
    Break1Start: "Break1Start",
    Break1End: "Break1End",
    LunchStart: "LunchStart",
    LunchEnd: "LunchEnd",
    Break2Start: "Break2Start",
    Break2End: "Break2End",
    Notes: "Notes",
};

const bundleVal = (row, semanticKey, extraIndex, altKey) =>
    trimmed(row[semanticKey]) || (altKey ? trimmed(row[altKey]) : "") || (extraIndex ? trimmed(row[`Extra${extraIndex}`]) : "") || null;

/** Map API/CSV Extra columns into UI field keys. */
export const bundleRowFromStorage = (row) => {
    if (!row) return row;
    const entity = resolveBundleType(row.MigrationType);
    const out = { ...row };
    if (entity === "employee_group") out.IsContractEmployee = bundleVal(row, "IsContractEmployee", 1);
    else if (entity === "reimbursement_type") out.ExpenseCategory = bundleVal(row, "ExpenseCategory", 1);
    else if (entity === "asset_sub_category") out.AssetCategory = bundleVal(row, "AssetCategory", 1, "ParentAssetCategory");
    else if (entity === "shift") {
        out.ShiftFrom = bundleVal(row, "ShiftFrom", 1);
        out.ShiftTo = bundleVal(row, "ShiftTo", 2);
        out.TotalShiftHrs = bundleVal(row, "TotalShiftHrs", 3);
        out.TotalBreakHrs = bundleVal(row, "TotalBreakHrs", 4);
        out.Break1Start = bundleVal(row, "Break1Start", 5);
        out.Break1End = bundleVal(row, "Break1End", 6);
        out.LunchStart = bundleVal(row, "LunchStart", 7);
        out.LunchEnd = bundleVal(row, "LunchEnd", 8);
        out.Break2Start = bundleVal(row, "Break2Start", 9);
        out.Break2End = bundleVal(row, "Break2End", 10);
    }
    return out;
};

/** Map UI field keys back to Extra columns for API/CSV storage. */
export const bundleRowToStorage = (row) => {
    const entity = resolveBundleType(row.MigrationType);
    const out = {
        MigrationType: row.MigrationType,
        Name: row.Name,
        Notes: row.Notes ?? null,
        Extra1: null,
        Extra2: null,
        Extra3: null,
        Extra4: null,
        Extra5: null,
        Extra6: null,
        Extra7: null,
        Extra8: null,
        Extra9: null,
        Extra10: null,
    };
    if (row.id != null) out.id = row.id;

    if (entity === "employee_group") out.Extra1 = row.IsContractEmployee ?? null;
    else if (entity === "reimbursement_type") out.Extra1 = row.ExpenseCategory ?? null;
    else if (entity === "asset_sub_category") out.Extra1 = row.AssetCategory ?? null;
    else if (entity === "shift") {
        out.Extra1 = row.ShiftFrom ?? null;
        out.Extra2 = row.ShiftTo ?? null;
        out.Extra3 = row.TotalShiftHrs ?? null;
        out.Extra4 = row.TotalBreakHrs ?? null;
        out.Extra5 = row.Break1Start ?? null;
        out.Extra6 = row.Break1End ?? null;
        out.Extra7 = row.LunchStart ?? null;
        out.Extra8 = row.LunchEnd ?? null;
        out.Extra9 = row.Break2Start ?? null;
        out.Extra10 = row.Break2End ?? null;
    }
    return out;
};

const BUNDLE_TYPE_EXTRA_KEYS = {
    employee_group: ["IsContractEmployee"],
    reimbursement_type: ["ExpenseCategory"],
    asset_sub_category: ["AssetCategory"],
    shift: [
        "ShiftFrom", "ShiftTo", "TotalShiftHrs", "TotalBreakHrs",
        "Break1Start", "Break1End", "LunchStart", "LunchEnd", "Break2Start", "Break2End",
    ],
};

/** Review grid: base columns + only the extra fields used by types in this batch. */
export const getBundleReviewFields = (rows, allFields) => {
    const baseKeys = new Set(["MigrationType", "Name", "Notes"]);
    const extraKeys = new Set();
    for (const row of rows || []) {
        const t = resolveBundleType(row.MigrationType);
        (BUNDLE_TYPE_EXTRA_KEYS[t] || []).forEach((k) => extraKeys.add(k));
    }
    return allFields.filter((f) => baseKeys.has(f.key) || extraKeys.has(f.key));
};

export const masterMigrationTitle = (config) =>
    config?.isBundle ? config.label : `${config?.label} Migration`;

const BUNDLE_DETAIL_FIELDS = [
    MASTER_MIGRATION_TYPES.employee_group.fields.find((f) => f.key === "IsContractEmployee"),
    MASTER_MIGRATION_TYPES.reimbursement_type.fields.find((f) => f.key === "ExpenseCategory"),
    MASTER_MIGRATION_TYPES.asset_sub_category.fields.find((f) => f.key === "AssetCategory"),
    ...MASTER_MIGRATION_TYPES.shift.fields.filter((f) => !["ShiftName", "Notes"].includes(f.key)),
].filter(Boolean);

const bundleRowToEntityRow = (entityType, row) => {
    const name = trimmed(row.Name);
    const notes = row.Notes ?? null;

    switch (entityType) {
        case "user_group":
            return { UserGroupName: name, Notes: notes };
        case "employee_type":
            return { EmployeeTypeName: name, Notes: notes };
        case "department":
            return { DepartmentName: name, Notes: notes };
        case "designation":
            return { DesignationName: name, Notes: notes };
        case "employee_group":
            return {
                EmployeeGroupName: name,
                IsContractEmployee: bundleVal(row, "IsContractEmployee", 1),
                Notes: notes,
            };
        case "reimbursement_type":
            return {
                ReimbursementTypeName: name,
                ExpenseCategory: bundleVal(row, "ExpenseCategory", 1) || "general",
                Notes: notes,
            };
        case "asset_type":
            return { AssetTypeName: name, Notes: notes };
        case "asset_category":
            return { CategoryName: name, Notes: notes };
        case "asset_sub_category":
            return {
                AssetSubCategoryName: name,
                AssetCategory: bundleVal(row, "AssetCategory", 1, "ParentAssetCategory"),
                Notes: notes,
            };
        case "shift":
            return {
                ShiftName: name,
                Notes: notes,
                ShiftFrom: bundleVal(row, "ShiftFrom", 1),
                ShiftTo: bundleVal(row, "ShiftTo", 2),
                TotalShiftHrs: bundleVal(row, "TotalShiftHrs", 3),
                TotalBreakHrs: bundleVal(row, "TotalBreakHrs", 4),
                Break1Start: bundleVal(row, "Break1Start", 5),
                Break1End: bundleVal(row, "Break1End", 6),
                LunchStart: bundleVal(row, "LunchStart", 7),
                LunchEnd: bundleVal(row, "LunchEnd", 8),
                Break2Start: bundleVal(row, "Break2Start", 9),
                Break2End: bundleVal(row, "Break2End", 10),
            };
        default:
            return {};
    }
};

const validateBundleType = (v) =>
    resolveBundleType(v) ? null : "Enter a valid migration type (e.g. department, user_group, shift)";

export const MASTER_BUNDLE_MIGRATION = buildConfig({
    type: "master_bundle",
    slug: "all-in-one",
    label: "Master Migration",
    subtitle: "Import all master records from one CSV file.",
    requiredHeader: "Migration Type *",
    isBundle: true,
    optionSources: MASTER_OPTION_SOURCES,
    fetchOptions: async (fetch) => {
        const res = await fetch(MASTER_ASSET_CATEGORY, { drop_down: "True" });
        const list = res?.status === "success" && Array.isArray(res.data) ? res.data : [];
        return { asset_category: list };
    },
    fields: [
        {
            key: "MigrationType",
            label: "Migration Type",
            required: true,
            kind: "enum",
            enum: bundleTypeEnum,
            validate: validateBundleType,
        },
        { key: "Name", label: "Name", required: true, kind: "text", validate: text(60) },
        notesField,
        ...BUNDLE_DETAIL_FIELDS,
    ],
});

MASTER_BUNDLE_MIGRATION.deserializeRow = bundleRowFromStorage;
MASTER_BUNDLE_MIGRATION.serializeRow = bundleRowToStorage;
MASTER_BUNDLE_MIGRATION.getReviewFields = getBundleReviewFields;
MASTER_BUNDLE_MIGRATION.headerToKey = (header) => {
    const label = String(header || "").replace(/\*/g, "").trim();
    if (label === "Parent Asset Category") return "AssetCategory";
    return label.replace(/\s+/g, "");
};

// Override validateRow for bundle — delegate to per-entity validators.
{
    const baseValidate = MASTER_BUNDLE_MIGRATION.validateRow;
    MASTER_BUNDLE_MIGRATION.validateRow = (row, options = {}) => {
        const entityType = resolveBundleType(row.MigrationType);
        if (!entityType) {
            const errors = {};
            const msg = validateBundleType(row.MigrationType);
            if (msg) errors.MigrationType = msg;
            if (Array.isArray(row?.comments)) {
                for (const c of row.comments) {
                    if (c.key === "migration_type" && !errors.MigrationType) {
                        errors.MigrationType = c.message;
                    }
                }
            }
            return errors;
        }

        const entityConfig = MASTER_MIGRATION_TYPES[entityType];
        const mapped = bundleRowToEntityRow(entityType, row);
        const entityErrors = entityConfig.validateRow(mapped, options);
        const errors = {};
        for (const [key, msg] of Object.entries(entityErrors)) {
            const bundleKey = ENTITY_FIELD_TO_BUNDLE[key] || key;
            if (!errors[bundleKey]) errors[bundleKey] = msg;
        }

        if (Array.isArray(row?.comments)) {
            for (const c of row.comments) {
                if (c.key === "migration_type") {
                    if (row.MigrationType === c.value && !errors.MigrationType) {
                        errors.MigrationType = c.message;
                    }
                    continue;
                }
                const pascal = SNAKE_TO_PASCAL[c.key];
                const bundleKey = pascal ? (ENTITY_FIELD_TO_BUNDLE[pascal] || pascal) : null;
                if (bundleKey && row[bundleKey] === c.value && !errors[bundleKey]) {
                    errors[bundleKey] = c.message;
                }
            }
        }

        return errors;
    };
    MASTER_BUNDLE_MIGRATION.rowHasErrors = (row, options) =>
        Object.keys(MASTER_BUNDLE_MIGRATION.validateRow(row, options)).length > 0;
    void baseValidate;
}

export const MASTER_MIGRATION_SLUGS = [
    MASTER_BUNDLE_MIGRATION.slug,
    ...Object.values(MASTER_MIGRATION_TYPES).map((c) => c.slug),
];

export const getMasterMigrationBySlug = (slug) => {
    if (slug === MASTER_BUNDLE_MIGRATION.slug) return MASTER_BUNDLE_MIGRATION;
    return Object.values(MASTER_MIGRATION_TYPES).find((c) => c.slug === slug);
};

export const getMasterMigrationByType = (type) => MASTER_MIGRATION_TYPES[type];

/** Sidebar + hub order — listed below Employee Migration. */
export const MASTER_MIGRATION_NAV_ORDER = [
    "user_group",
    "employee_type",
    "department",
    "designation",
    "employee_group",
    "reimbursement_type",
    "asset_type",
    "asset_category",
    "asset_sub_category",
    "shift",
];

export const getMasterMigrationNavEntries = () => [
    {
        type: MASTER_BUNDLE_MIGRATION.type,
        slug: MASTER_BUNDLE_MIGRATION.slug,
        label: MASTER_BUNDLE_MIGRATION.label,
        subtitle: MASTER_BUNDLE_MIGRATION.subtitle,
        href: `/datamigration/master/${MASTER_BUNDLE_MIGRATION.slug}`,
    },
];

/** Labels for admin nav defaults (must match sidebar sub-item labels). */
export const MASTER_MIGRATION_DEFAULT_LABELS = ["master migration"];

/** Sample CSV files (in public/migration-samples/) for testing migrations. */
export const MIGRATION_SAMPLE_FILES = {
    employee: "/migration-samples/employee-migration-sample.csv",
};

export const getMigrationSampleUrl = (type) => MIGRATION_SAMPLE_FILES[type] || null;

let _rowKeySeq = 0;
export const ensureMasterKeys = (rows = []) =>
    rows.map((r) => (r.__key != null ? r : { ...r, __key: r.id != null ? `id-${r.id}` : `n-${++_rowKeySeq}` }));
